import { ReactNode, useEffect, useRef, useState } from "react";

export function ContentMinimap({
  children,
  count,
}: {
  count: number;
  children?: ReactNode;
}) {
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <div className="grid gap-4">
      <div className="grid" ref={contentRef}>
        {children}
      </div>
      <Minimap
        count={count}
        contentRef={contentRef.current?.firstElementChild}
      />
    </div>
  );
}

function Minimap({
  contentRef,
  count,
}: {
  contentRef: Element | undefined | null;
  count: number;
}) {
  const minimapRef = useRef<HTMLDivElement>(null);
  const [viewportInfo, setViewportInfo] = useState({
    containerWidth: 0,
    contentWidth: 0,
    scrollLeft: 0,
    viewportRatio: 1,
  });

  useEffect(() => {
    const updateViewportInfo = () => {
      if (contentRef && minimapRef.current) {
        const container = contentRef;
        const { clientWidth, scrollWidth, scrollLeft } = container;

        // Calculate what percentage of the total content is visible
        const viewportRatio = clientWidth / scrollWidth;

        setViewportInfo({
          containerWidth: clientWidth,
          contentWidth: scrollWidth,
          scrollLeft,
          viewportRatio: Math.min(viewportRatio, 1),
        });
      }
    };

    // Update initially and on resize
    updateViewportInfo();
    window.addEventListener("resize", updateViewportInfo);

    // Track scrolling
    const contentElement = contentRef;
    if (contentElement) {
      contentElement.addEventListener("scroll", updateViewportInfo);
    }

    return () => {
      window.removeEventListener("resize", updateViewportInfo);
      if (contentElement) {
        contentElement.removeEventListener("scroll", updateViewportInfo);
      }
    };
  }, [contentRef]);

  const handleMinimapClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (contentRef && minimapRef.current) {
      const minimap = minimapRef.current;
      const rect = minimap.getBoundingClientRect();

      // Get click position relative to minimap
      const clickX = e.clientX - rect.left;

      // Calculate position as percentage of minimap width
      const clickPercentage = clickX / rect.width;

      // Apply scroll position - center the viewport on the clicked position
      const scrollMax = viewportInfo.contentWidth - viewportInfo.containerWidth;
      const targetScrollLeft = Math.max(
        0,
        Math.min(
          clickPercentage * viewportInfo.contentWidth -
            viewportInfo.containerWidth / 2,
          scrollMax,
        ),
      );

      contentRef.scrollLeft = targetScrollLeft;
    }
  };

  const handleMinimapDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.buttons === 1) {
      // Left mouse button
      handleMinimapClick(e);
    }
  };

  const handleTouchEvent = (e: React.TouchEvent<HTMLDivElement>) => {
    if (contentRef && minimapRef.current) {
      if (e.cancelable) e.preventDefault(); // Prevent scrolling the page
      const minimap = minimapRef.current;
      const rect = minimap.getBoundingClientRect();

      // Get touch position relative to minimap
      const touchX = e.touches[0].clientX - rect.left;

      // Calculate position as percentage of minimap width
      const touchPercentage = touchX / rect.width;

      // Apply scroll position - center the viewport on the touched position
      const scrollMax = viewportInfo.contentWidth - viewportInfo.containerWidth;
      const targetScrollLeft = Math.max(
        0,
        Math.min(
          touchPercentage * viewportInfo.contentWidth -
            viewportInfo.containerWidth / 2,
          scrollMax,
        ),
      );

      contentRef.scrollLeft = targetScrollLeft;
    }
  };

  // Only show minimap if content is wider than container
  const showMinimap = viewportInfo.viewportRatio < 1;

  // Calculate the indicator width and position
  const indicatorWidth = `${viewportInfo.viewportRatio * 100}%`;
  const indicatorPosition =
    viewportInfo.contentWidth > 0
      ? (viewportInfo.scrollLeft /
          (viewportInfo.contentWidth - viewportInfo.containerWidth)) *
        (1 - viewportInfo.viewportRatio) *
        100
      : 0;

  return (
    <div
      className={`sticky bottom-5 h-16 w-1/3 place-self-end rounded-lg border bg-muted p-2 ${showMinimap ? "block" : "hidden"}`}
    >
      <div
        ref={minimapRef}
        className="flex h-full w-full cursor-grab items-stretch gap-2"
        onClick={handleMinimapClick}
        onMouseMove={handleMinimapDrag}
        onTouchStart={handleTouchEvent}
        onTouchMove={handleTouchEvent}
      >
        {new Array(count).fill(0).map((_, i) => (
          <div
            key={i}
            style={{
              width: `${(1 / count) * 100}%`,
            }}
            className="h-full rounded-sm bg-muted-foreground"
          />
        ))}
        <div
          className="absolute top-0 h-full rounded-sm bg-primary/40"
          style={{
            width: indicatorWidth,
            left: `${indicatorPosition}%`,
          }}
        />
      </div>
    </div>
  );
}
